import clsx from 'clsx';
import { motion, useAnimation } from 'motion/react';
import { useEffect, useState, useCallback } from 'react';

import CityIcon from '@common/assets/icons/city.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import PencilIcon from '@common/assets/icons/pencil.svg?react';
import { usePreloadImages } from '@common/hooks';
import { Badge } from '@common/ui/Badge';
import { CachedImage } from '@common/ui/CachedImage';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { useSwipeAnimation } from '@entities/dating/hooks/useSwipeAnimation';
import * as dicts from '@entities/profile/dicts';

import { type User } from '@services/UsersService';

import { ProfileCardSkeleton } from './components/ProfileCardSkeleton';
import styles from './ProfileCard.module.scss';

export interface ProfileCardProps {
  profile: User | undefined;
  canSwipes?: boolean;
  backButton?: React.ReactNode;
  filtersButton?: React.ReactNode;
  onLike?: (profile: User) => void;
  onSkip?: (profile: User) => void;
  style?: React.CSSProperties;
}

const matchText = { 0: 'Вы ему нравитесь', 1: 'Вы ей нравитесь' };
const likeText = { 0: 'Поставим ему лайк?', 1: 'Поставим ей лайк?' };

const truncateText = (text: string, maxLength: number) =>
  text.length <= maxLength ? text : text.slice(0, maxLength) + '...';

export const ProfileCard = ({
  profile,
  canSwipes = true,
  backButton = false,
  filtersButton = false,
  onLike,
  onSkip,
  style,
}: ProfileCardProps) => {
  const controls = useAnimation();
  const auth = useAuthStore();
  const { preloadImages, imagesPreloaded } = usePreloadImages();

  const {
    dislikeGradientRef,
    handleDrag,
    handleDragEnd,
    likeGradientRef,
    opacity,
    rotate,
    x,
  } = useSwipeAnimation({ onLike, onSkip, profile });

  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    preloadImages(profile?.photos ?? []);
  }, [profile?.photos, preloadImages]);

  const onCardClick = useCallback(
    (side: 'left' | 'right') => {
      setCurrentPage((prevPage) => {
        const total = profile?.photos?.length || 1;

        if (side === 'left') return prevPage === 0 ? total - 1 : prevPage - 1;

        return (prevPage + 1) % total;
      });
    },
    [profile?.photos],
  );

  return (
    <motion.div
      data-test-id="profile-card-container"
      className={styles.profileCard}
      style={{
        x,
        rotate,
        opacity,
        ...style,
      }}
      drag={canSwipes && imagesPreloaded ? 'x' : false}
      animate={controls}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
    >
      <div className={styles.pageNavigator}>
        <div
          className={styles.left}
          aria-label="Предыдущее фото"
          data-test-id="profile-card-page-left"
          onClick={() => onCardClick('left')}
        />
        <div
          className={styles.right}
          aria-label="Следующее фото"
          data-test-id="profile-card-page-right"
          onClick={() => onCardClick('right')}
        />
      </div>

      <div className={styles.gradient} data-type="default" />

      <div ref={likeGradientRef} className={styles.gradient} data-type="like">
        <div className={styles.gradientIconContainer}>
          <CachedImage
            testId="cached-image"
            alt="badge"
            src="/icons/3d-icons/like.png"
            style={{ width: '216px' }}
          />
        </div>
      </div>
      <div
        ref={dislikeGradientRef}
        className={styles.gradient}
        data-type="dislike"
      >
        <div className={styles.gradientIconContainer}>
          <CachedImage
            testId="cached-image"
            alt="badge"
            src="/icons/3d-icons/dislike.png"
            style={{ width: '216px' }}
          />
        </div>
      </div>

      <div className={styles.pageTabs}>
        {(profile?.photos ?? []).map((_, index) => (
          <div
            key={index}
            className={styles.tab}
            data-active={index === currentPage}
          />
        ))}
      </div>

      {backButton && <div className={styles.back}>{backButton}</div>}
      <div className={styles.filters}>{filtersButton}</div>

      {imagesPreloaded ? (
        (profile?.photos ?? []).map((photo, index) => (
          <div
            key={index}
            className={styles.imageContainer}
            style={{ opacity: index === currentPage ? 1 : 0 }}
          >
            <img src={photo} className={styles.image} alt="Фото пользователя" />
          </div>
        ))
      ) : (
        <ProfileCardSkeleton />
      )}

      <div className={clsx(styles.content, !imagesPreloaded && styles.loading)}>
        {profile?.liked_me && auth?.user?.is_premium && (
          <Badge
            testId="profile-card-liked-me-badge"
            style={{ zoom: 0.7 }}
            color="action"
            prefix={<HeartIcon />}
            text={matchText[profile?.sex]}
          />
        )}

        <div className={styles.name}>
          {profile?.username ? `${truncateText(profile.username, 13)}, ` : null}
          {profile?.age}
          <span className={styles.additional}>
            {profile?.is_premium && (
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/fire.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
            {profile?.is_verified && (
              <CachedImage
                testId="cached-image"
                alt="badge"
                src="/icons/3d-icons/confirmed.png"
                blur="2px"
                style={{ width: '24px' }}
              />
            )}
          </span>
        </div>

        {currentPage === 0 && profile?.city && (
          <div className={styles.city} data-test-id="profile-card-city">
            <CityIcon /> {truncateText(profile.city, 15)}
          </div>
        )}

        {profile && (
          <div className={styles.about}>
            {currentPage === 0 && (
              <span data-test-id="profile-card-about">
                <PencilIcon />
                <span>{truncateText(profile.about, 200)}</span>
              </span>
            )}
            {currentPage === 1 && (
              <span data-test-id="profile-card-goal">
                {dicts.goalDisplayDict[profile.goal]}
              </span>
            )}
            {currentPage === 2 && (
              <span data-test-id="profile-card-like-question">
                {likeText[profile.sex]}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
